import { Box, Paper, Stack, Typography, type PaperProps } from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import React from "react";

interface AdminSectionPanelProps {
  children: React.ReactNode;
  /** Optional, fully composable section content such as a title, labels, or controls. */
  header?: React.ReactNode;
  /** Standard section title. Use with icon and actions rather than composing a custom header. */
  title?: React.ReactNode;
  /** Standard section title icon. */
  icon?: React.ReactNode;
  /** Standard trailing header controls. */
  actions?: React.ReactNode;
  elevation?: PaperProps["elevation"];
  headerSx?: SxProps<Theme>;
  paperProps?: Omit<PaperProps, "children" | "elevation" | "sx">;
  paperSx?: SxProps<Theme>;
}

/** Shared admin paper section with Debug-style spacing around its header and content. */
export const AdminSectionPanel: React.FC<AdminSectionPanelProps> = ({
  children,
  header,
  title,
  icon,
  actions,
  elevation = 1,
  headerSx,
  paperProps,
  paperSx,
}) => (
  <Paper
    elevation={elevation}
    sx={[
      { overflow: "hidden", p: 2 },
      ...(paperSx ? (Array.isArray(paperSx) ? paperSx : [paperSx]) : []),
    ]}
    {...paperProps}
  >
    {(header || title) && (
      <Box
        data-pagination-region="top"
        sx={[{ mb: 2 }, ...(headerSx ? (Array.isArray(headerSx) ? headerSx : [headerSx]) : [])]}
      >
        {header ?? (
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              color="primary.main"
              sx={{ minWidth: 0 }}
            >
              {icon}
              <Typography
                variant="h6"
                color="inherit"
                sx={{ fontSize: { xs: "1.1rem", sm: "1.15rem" } }}
              >
                {title}
              </Typography>
            </Stack>
            {actions}
          </Stack>
        )}
      </Box>
    )}
    {children}
  </Paper>
);
